import { NewsItem } from "./newsItems/NewsItem";
import { NewsItemRenterFixed, NewsItemRenterFixedOptions } from "./newsItems/NewsItemRenterFixed";
import { NewsItemRaidWithShield, NewsItemRaidWithShieldOptions } from "./newsItems/NewsItemRaidWithShield";
import { NewsItemRaidWithoutShield, NewsItemRaidWithoutShieldOptions } from "./newsItems/NewsItemRaidWithoutShield";
import { NewsItemRelocate, NewsItemRelocateOptions } from "./newsItems/NewsItemRelocate";
import { NewsItemSteal, NewsItemStealOptions } from "./newsItems/NewsItemSteal";
import { NewsItemRenterUpgraded, NewsItemRenterUpgradedOptions } from "./newsItems/NewsItemRenterUpgraded";
import { NewsItemVisit, NewsItemVisitOptions } from "./newsItems/NewsItemVisit";
import { NewsItemRankUpLandlord, NewsItemRankUpLandlordOptions } from "./newsItems/NewsItemRankUpLandlord";
import { NewsItemRankUpRenter, NewsItemRankUpRenterOptions } from "./newsItems/NewsItemRankUpRenter";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { LatestNewsItem } from "../../../common/models/latestnews/LatestNewsItem";
import { LatestNewsType } from "../../../common/models/latestnews/LatestNewsType";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { Texture } from "pixi.js";
import { NewsItemUpgradeOptions, NewsItemUpgraded } from "./newsItems/NewsItemUpgraded";
import { NewsItemFixOptions, NewsItemFixed } from "./newsItems/NewsItemFixed";
import { NewsItemBuildingSold, NewsItemBuildingSoldOptions } from "./newsItems/NewsItemBuildingSold";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CancellationToken } from "async/CancellationToken";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { NewsItemPurchaseType, NewsItemPurchasing, NewsItemPurchasingOptions } from "./newsItems/NewsItemPurchasing";
import { LatestNewsResourcePackage } from "../resources/LatestNewsResourcePackage";
import { ILatestNewsController } from "../controllers/ILatestNewsController";
import { NewsItemMintReward, NewsItemMintRewardOptions } from "./newsItems/NewsItemMintReward";
import { Web3Config } from "../../../../../web3/config/Web3Config";

export class NewsItemsFactory {
    private readonly newsResources: LatestNewsResourcePackage;
    private readonly newsController: ILatestNewsController;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly playerModel: IPlayerModel;
    private readonly guestModel: IGuestModel;
    private readonly newsDynamicResources: DynamicResourcesPackage;
    private readonly web3Config: Web3Config;
    private readonly cancellationToken: CancellationToken;
    private readonly logger: ILogger;

    public constructor(
        newsResources: LatestNewsResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        newsController: ILatestNewsController,
        playerModel: IPlayerModel,
        guestModel: IGuestModel,
        newsDynamicResources: DynamicResourcesPackage,
        web3Config: Web3Config,
        cancellationToken: CancellationToken,
        logger: ILogger
    ) {
        this.newsResources = newsResources;
        this.userProfileResources = userProfileResources;
        this.newsController = newsController;
        this.playerModel = playerModel;
        this.guestModel = guestModel;
        this.newsDynamicResources = newsDynamicResources;
        this.web3Config = web3Config;
        this.cancellationToken = cancellationToken;
        this.logger = logger;
    }

    public createItem(latestNewsItem: LatestNewsItem): NewsItem {
        const avatarTexture: Texture = latestNewsItem.guest?.avatar ? this.newsDynamicResources.resource(latestNewsItem.guest?.avatar) : this.guestModel.avatarTexture.value;
        switch (latestNewsItem.type) {
            case LatestNewsType.RAID_WITH_SHIELDS: {
                return new NewsItemRaidWithShield(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemRaidWithShieldOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture)
                );
            }
            case LatestNewsType.RAID_WITH_DOWNGRADE: {
                return new NewsItemRaidWithoutShield(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemRaidWithoutShieldOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture, latestNewsItem.downgradedElements)
                );
            }
            case LatestNewsType.VISIT: {
                return new NewsItemVisit(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemVisitOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture, latestNewsItem.rewardInCoins)
                );
            }
            case LatestNewsType.BUILDING_RANK_UP: {
                if (this.playerModel.buildingRights.value == BuildingRightsType.RENTER) {
                    return new NewsItemRankUpRenter(
                        latestNewsItem.id,
                        this.newsResources,
                        this.newsController,
                        new NewsItemRankUpRenterOptions(latestNewsItem.rewardInCoins, latestNewsItem.buildingRankUpLevel),
                        this.cancellationToken
                    );
                } else {
                    return new NewsItemRankUpLandlord(
                        latestNewsItem.id,
                        this.newsResources,
                        this.newsController,
                        new NewsItemRankUpLandlordOptions(latestNewsItem.buildingRankUpLevel, latestNewsItem.rewardInCoins, latestNewsItem.buildingRankUpLevel),
                        this.cancellationToken
                    );
                }
            }
            case LatestNewsType.RENTER_POLICY_VIOLATION: {
                return new NewsItemRelocate(
                    latestNewsItem.id,
                    this.newsResources,
                    this.newsController,
                    new NewsItemRelocateOptions(false)
                );
            }
            case LatestNewsType.RENTER_POLICY_VIOLATION_WITH_INSURANCE: {
                return new NewsItemRelocate(
                    latestNewsItem.id,
                    this.newsResources,
                    this.newsController,
                    new NewsItemRelocateOptions(true)
                );
            }
            case LatestNewsType.RETURNED_TOKENS_FOR_RENTER_FIX: {
                return new NewsItemRenterFixed(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemRenterFixedOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture, latestNewsItem.buildingAddress, latestNewsItem.rewardInTokens)
                );
            }
            case LatestNewsType.RETURNED_TOKENS_FOR_RENTER_UPGRADE: {
                return new NewsItemRenterUpgraded(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemRenterUpgradedOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture, latestNewsItem.buildingAddress, latestNewsItem.rewardInTokens)
                );
            }
            case LatestNewsType.STEAL: {
                return new NewsItemSteal(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemStealOptions(latestNewsItem.guest?.name.toUpperCase(), avatarTexture, latestNewsItem.stealAmountInCoins)
                );
            }
            case LatestNewsType.RETURNED_TOKENS_FOR_FIX: {
                return new NewsItemFixed(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    this.newsController,
                    new NewsItemFixOptions(avatarTexture, latestNewsItem.rewardInTokens),
                    this.cancellationToken
                );
            }
            case LatestNewsType.RETURNED_TOKENS_FOR_UPGRADE: {
                return new NewsItemUpgraded(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    this.newsController,
                    new NewsItemUpgradeOptions(avatarTexture, latestNewsItem.rewardInTokens),
                    this.cancellationToken
                );
            }
            case LatestNewsType.HOLDER_SOLD_BUILDING: {
                return new NewsItemBuildingSold(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemBuildingSoldOptions(avatarTexture, latestNewsItem.buildingAddress, latestNewsItem.price.usd),
                    this.web3Config
                );
            }
            case LatestNewsType.SUCCESSFUL_GOLDEN_WHEEL_PURCHASE: {
                return new NewsItemPurchasing(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemPurchasingOptions(avatarTexture, NewsItemPurchaseType.GOLDEN_WHEEL),
                    this.web3Config
                );
            }
            case LatestNewsType.SUCCESSFUL_BUILDING_TRANSFER: {
                return new NewsItemPurchasing(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemPurchasingOptions(avatarTexture, NewsItemPurchaseType.BUILDING),
                    this.web3Config
                );
            }
            case LatestNewsType.SUCCESSFUL_DICE_ROLLS_PURCHASE: {
                return new NewsItemPurchasing(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemPurchasingOptions(avatarTexture, NewsItemPurchaseType.DICE_ROLLS),
                    this.web3Config
                );
            }
            case LatestNewsType.PIGGY_CLUB_MINT_REWARD: {
                return new NewsItemMintReward(
                    latestNewsItem.id,
                    this.newsResources,
                    this.userProfileResources,
                    new NewsItemMintRewardOptions(avatarTexture, latestNewsItem.piggyBankRewardInTokens)
                );
            }
            default: {
                this.logger.warn(`Not implemented news item type: ${latestNewsItem.type}`);
                return null;
            }
        }
    }
}
